/* oxlint-disable typescript/consistent-type-assertions */
import type { CamelCasedPropertiesDeep, JsonPrimitive } from 'type-fest';
import { decamelize } from '../decamelize';

const STRING_CAMELIZE_REGEXP = /(?<separator>-|_|\.|\s)+(?<char>.)?/g;
export const camelizeKey = (key: string): string =>
  key.replace(STRING_CAMELIZE_REGEXP, (_, __, char: string) => (char ? char.toUpperCase() : ''));

interface NestedObject {
  [key: string]: NestedObject | JsonPrimitive | NestedObject[] | JsonPrimitive[] | undefined;
}

function isNestedObject(value: unknown): value is NestedObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNestedObjectArray(value: unknown): value is NestedObject[] {
  return Array.isArray(value) && value.every(isNestedObject);
}

function camelizeKeysArray<T>(array: T[]): CamelCasedPropertiesDeep<T>[] {
  return array.map(item => camelizeKeys(item)) as CamelCasedPropertiesDeep<T>[];
}

export function camelizeKeys<T>(obj: T): CamelCasedPropertiesDeep<T> {
  if (Array.isArray(obj)) {
    return camelizeKeysArray(obj) as CamelCasedPropertiesDeep<T>;
  }
  if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
      const value = (obj as Record<string, unknown>)[key];
      const camelizedKey = camelizeKey(key);
      if (isNestedObject(value)) {
        return { ...acc, [camelizedKey]: camelizeKeys(value) };
      }
      if (isNestedObjectArray(value)) {
        return { ...acc, [camelizedKey]: value.map(camelizeKeys) };
      }
      return { ...acc, [camelizedKey]: value };
    }, {}) as CamelCasedPropertiesDeep<T>;
  }
  return obj as CamelCasedPropertiesDeep<T>;
}

type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

export type SnakeToCamelCaseObject<T> = {
  [K in keyof T as K extends string ? SnakeToCamelCase<K> : K]: T[K];
};

/**
 * Normalize the keys of an object from snake_case to camelCase
 * Leaves nested objects as is
 * @param obj - The object to normalize
 * @returns The normalized object
 */
export function snakeToCamelCaseShallow<T extends Record<string, unknown>>(
  obj: T
): SnakeToCamelCaseObject<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const camelKey = camelizeKey(key);
    return {
      ...acc,
      [camelKey]: value,
    };
  }, {}) as SnakeToCamelCaseObject<T>;
}

/**
 * Normalize the keys of an object from camelCase to snake_case.
 * Leaves nested objects as is (shallow conversion only).
 *
 * @param obj - The object to normalize, or null/undefined
 * @returns A new object with snake_case keys, or the same null/undefined
 */
export function camelToSnakeCaseShallow<T extends object>(
  obj: T | null | undefined
): Record<string, unknown> | null | undefined {
  if (obj === null || obj === undefined) return obj;
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [decamelize(k), v]));
}
